import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Button, SvgIconHandler } from "../../atoms";
import { STATIC_KEYWORDS } from "../../../constants";
import { SocialShareTemplate } from "../../../arrow";
import { SocialShare } from "@quintype/components";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { TableView } from "./table-view";
import { YearDropdown } from "../mpw-detail-page/year-drop-down";
// import { RankingColumns, RankingSubColumns } from "./ranking-columns";
import SearchIcon from "../rankings-table/search";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { TOPMOST_CELEBRITIES_DESCRIPTIONS } from "../../../../../config/entity-ranking-descriptions";
import { trackLoadMoreClick } from "../../utils";

import "./ranking-table.m.css";

export const topMostCelebrities = ({ data }) => {
  const { staticData, rankingEntitySlug, entitiesByYearJson, activeYear, snapshotByRank, totalSnapshotCount, fullDataUrl } = data;

  const initialSnapshots = snapshotByRank?.snapshots || [];

  const { label, top15Label } = staticData;
  const description = TOPMOST_CELEBRITIES_DESCRIPTIONS[activeYear];
  const { LOAD_MORE, READ_MORE, NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  const isWebView = get(qtState, ["config", "isWebView"]) === true;

  const [enableLoadMore, setEnableLoadMore] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [yearDropdownActive, setYearDropdownActive] = useState(false);
  const [visibleItemsForList, setVisibleItemsForList] = useState(10);
  const [showEntities, setShowEntities] = useState(initialSnapshots);
  const [searchCount, setSearchCount] = useState(totalSnapshotCount || initialSnapshots.length);
  const [fullUrl, setFullUrl] = useState("");
  const [currentView, setCurrentView] = useState("overall");
  const [fullSnapshots, setFullSnapshots] = useState(null);
  const [isFetchingFullData, setIsFetchingFullData] = useState(false);

  const subheadlineRef = useRef(null);
  const allSnapshots = fullSnapshots || initialSnapshots;

  const expandBio = () => setReadMoreClicked(true);

  // Track B-Schools page interactions
  let isTracking = false;
  const trackRankingInteraction = (action, value, position = null) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_ranking_detail_interactions",
        event_action: action,
        link_text: value,
        ...(position && { link_position: position }),
        category: "rankings",
        sub_category: rankingEntitySlug || "NA",
        template_name: "rankdetail",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const loadFullSnapshots = async () => {
    if (fullSnapshots) return fullSnapshots;
    if (!fullDataUrl) return initialSnapshots;

    try {
      setIsFetchingFullData(true);
      const response = await fetch(fullDataUrl);
      const json = await response.json();
      const snapshots = json?.snapshots || [];
      setFullSnapshots(snapshots);
      return snapshots;
    } catch (err) {
      console.error("Error fetching celebrities data:", err);
      return initialSnapshots;
    } finally {
      setIsFetchingFullData(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const searchTerm = searchInputValue;
    if (searchTerm && currentView === "overall" && !fullSnapshots) {
      await loadFullSnapshots();
    }
    setSearchText(searchTerm);
    if (searchTerm) {
      trackRankingInteraction("ranking_search", searchTerm.toLowerCase());
    }
  };

  const getMoreStories = async () => {
    if (isFetchingFullData) return;

    trackLoadMoreClick({
      linkCategory: `${rankingEntitySlug}-${activeYear}`,
      category: "ranking",
      subCategory: rankingEntitySlug,
      templateName: "rankdetail",
    });
    if (currentView === "overall" && !fullSnapshots) {
      await loadFullSnapshots();
    }
    setVisibleItemsForList((prev) => prev + 10);
    trackRankingInteraction("load_more", "load_more");
  };

  const includesCaseInsensitive = (str = "", searchTerm = "") =>
    String(str || "")
      .toLowerCase()
      .includes(
        String(searchTerm || "")
          .trim()
          .toLowerCase()
      );

  useEffect(() => {
    setVisibleItemsForList(10);
    if (typeof window !== "undefined") {
      setFullUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    const totalItems = searchText
      ? searchCount
      : fullSnapshots
        ? allSnapshots.length
        : totalSnapshotCount || allSnapshots.length;
    setEnableLoadMore(currentView === "overall" && visibleItemsForList < totalItems);
  }, [allSnapshots.length, currentView, fullSnapshots, searchCount, searchText, totalSnapshotCount, visibleItemsForList]);

  useEffect(() => {
    if (!isDesktop && subheadlineRef.current) {
      const bioHeight = window?.getComputedStyle(subheadlineRef.current);
      const getLineHeight = parseFloat(bioHeight.lineHeight);
      const lineHeightForThree = getLineHeight * 3;
      const contentHeight = subheadlineRef.current.scrollHeight;
      subheadlineRef.current.style.height = `${lineHeightForThree}px`;
      subheadlineRef.current.style.overflow = "hidden";

      if (readMoreClicked) {
        subheadlineRef.current.style.height = `${contentHeight}px`;
      }
    }
  }, [readMoreClicked, isDesktop]);

  useEffect(() => {
    const entities = allSnapshots;

    if (searchText) {
      const filterFn = (snapshot) => includesCaseInsensitive(snapshot.brand, searchText);
      const filteredSnapshots = entities.filter(filterFn);
      setSearchCount(filteredSnapshots.length);
      setShowEntities(filteredSnapshots);
    } else {
      setSearchCount(entities.length);
      setShowEntities(entities);
    }
  }, [allSnapshots, searchText]);

  const fetchData = async (group) => {
    try {
      const fetchJson = async (url) => {
        const response = await fetch(url);
        return response.json();
      };

      return group.urls.length === 1 ? await fetchJson(group.urls[0]) : await Promise.all(group.urls.map(fetchJson));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!yearDropdownActive || isDesktop) {
      document.body.classList.remove("no-scroll");
    } else if (yearDropdownActive && !isDesktop) {
      document.body.classList.add("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [yearDropdownActive]);

  useEffect(() => {
    if (currentView === "overall") {
      return;
    }

    const activeViewObj = topMostCelebritiesStaticData(activeYear).find((obj) => obj.key === currentView);

    setSearchText("");
    setSearchInputValue("");

    const fetchAndSetData = async () => {
      const data = await fetchData(activeViewObj);
      setShowEntities(data);
    };

    fetchAndSetData();
  }, [allSnapshots, currentView]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "rankings",
      sub_category: "top most valueble celebrities",
      content_id: "NA",
      template_name: "rankdetail",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  const renderTableView = (
    topmostValueCelebritiesList,
    title = null,
    // subColumns = RankingSubColumns
  ) => (
<>
      <TableView
        topmostValueCelebritiesList={topmostValueCelebritiesList}
        visibleItemsForList={visibleItemsForList}
        rankingEntitySlug={rankingEntitySlug}
        // columns={RankingColumns}
        currentView={currentView}
        // subcolumns={subColumns}
        trackClick={(item, viewType) =>
          trackRankingInteraction(
            viewType === "grid" ? "grid_item_click" : "list_item_click",
            item.brand || item.name || item.company,
            item.rank
          )
        }
      />
      </>
  );

  return (
    <div styleName="entity-detail-page">
      <div
        styleName="banner"><img src={!isDesktop ? "/images/banners/top-most-celebrities-mobile.png" : "/images/banners/top-most-celebrities-desktop.png"} alt={top15Label} title={top15Label} loading="lazy" width={1470} height={417} /></div>
      <div className="container" styleName="content-wrapper">
        <div styleName="header">
          <div styleName="header-content">
            <div styleName="headline">
              <span styleName="yeardrop">
                <YearDropdown
                  isDesktop={isDesktop}
                  yearDropdownActive={yearDropdownActive}
                  setYearDropdownActive={setYearDropdownActive}
                  yearData={[2026]}
                  rankingEntitySlug={rankingEntitySlug}
                  entitiesByYearJson={entitiesByYearJson}
                  page="top-billionaires"
                  activeYear={activeYear}
                  onYearChange={(year) => trackRankingInteraction("year_filter", year)}
                />
              </span>
              <h1 styleName="text">{`${label || top15Label} ${activeYear}`}</h1>
            </div>
            <div styleName="sub-headline clamped-text" ref={subheadlineRef}>
              {description}
            </div>
            {!readMoreClicked && !isDesktop && (
              <button onClick={expandBio} styleName="read-more">
                {READ_MORE}
              </button>
            )}
          </div>
        </div>
        <div styleName="search-row">
          <form onSubmit={handleSearch}>
            <div styleName="search">
              <div styleName="input">
                {searchText !== "" && searchInputValue !== "" && (
                  <span
                    onClick={() => {
                      setSearchInputValue("");
                      setSearchText("");
                    }}
                    styleName="best-Bschools-clearSearch"
                  >
                    <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
                  </span>
                )}
                <input
                  type="text"
                  name="search"
                  placeholder="Search"
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  styleName={searchInputValue !== "" ? "input-bar" : ""}
                />
              </div>
              <Button customStyleName={"bg-brand-primary"} onClickFunction={handleSearch}>
                {isDesktop ? "Search" : <SearchIcon />}
              </Button>
            </div>
          </form>
          {!isWebView && (
            <div styleName="list-grid-toggle">
              {/* <SortDropdown columns={columns} handleSort={handleSort} isDesktop={isDesktop} /> */}
              <SocialShare
                template={(props) =>
                  SocialShareTemplate({
                    ...props,
                    onShareClick: (platform) =>
                      trackRankingInteraction("share_click", platform?.toLowerCase() || "share"),
                  })
                }
                fullUrl={fullUrl}
                title={`${label} ${activeYear}`}
                size={18}
              />
            </div>
          )}
        </div>
        {!showEntities ||
        showEntities.length === 0 ||
        (showEntities.snapshots && showEntities.snapshots.length === 0) ? (
          <div styleName="oops-text">
            <span>
              {NO_RESULT_FOUND}'{searchText}' <br /> {TRY_AGAIN}
            </span>
          </div>
        ) : (
          <>{currentView === "overall" && renderTableView(showEntities)}</>
        )}
        {!isAdfreePage && (
          <ListingAdSlot
            adType="above_footer"
            pageType="indias-most-valuable-celebrities"
            adStyleName="ad-slot-size-728x90-300x250"
            index={3}
          />
        )}
      </div>
    </div>
  );
};

topMostCelebrities.propTypes = {
  data: PropTypes.object,
};
